import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function AirportAutocomplete({ value, onChange, placeholder, name }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const token = localStorage.getItem('token');

  const handleInputChange = async (e) => {
    const val = e.target.value.toUpperCase();
    onChange({ target: { value: val } });
    
    if (val.length < 1) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    try {
      const res = await fetch(`/api/airports?q=${encodeURIComponent(val)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSuggestions(data || []);
      setOpen(data && data.length > 0);
    } catch (err) {
      console.error('Airport search error:', err);
      setSuggestions([]);
    }
  };

  const handleSelect = (airport) => {
    onChange({ target: { value: airport.display } });
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div className="relative w-full">
      <input
        type="text"
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder}
        name={name}
        className="w-full p-2 border rounded"
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 bg-white border border-t-0 rounded-b shadow-lg z-10 max-h-48 overflow-y-auto">
          {suggestions.map((airport, idx) => (
            <div
              key={idx}
              onClick={() => handleSelect(airport)}
              className="px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm"
            >
              <div className="font-semibold">{airport.code}</div>
              <div className="text-gray-600 text-xs">{airport.name}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Flights() {
  const [flightNumber, setFlightNumber] = useState('');
  const [departure, setDeparture] = useState('');
  const [arrival, setArrival] = useState('');
  const [departureTime, setDepartureTime] = useState('');
  const [arrivalTime, setArrivalTime] = useState('');
  const [raceId, setRaceId] = useState('');
  const [races, setRaces] = useState([]);
  const [users, setUsers] = useState([]);
  const [flights, setFlights] = useState([]);
  const [loadingFlights, setLoadingFlights] = useState(true);
  const [status, setStatus] = useState('');
  const [filterRace, setFilterRace] = useState('');
  const [search, setSearch] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [editingFlightId, setEditingFlightId] = useState(null);
  const [editForm, setEditForm] = useState({ flight_number: '', departure: '', arrival: '', departure_time: '', arrival_time: '' });
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    // fetch races, flights and users in parallel
    const racesP = fetch('/api/races', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => []);
    const flightsP = fetch('/api/flights', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => []);
    const usersP = fetch('/api/users', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => []);
    const profileP = fetch('/api/profile', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => null);

    Promise.all([racesP, flightsP, usersP, profileP]).then(([rData, fData, uData, profileData]) => {
      setRaces(rData || []);
      setFlights(fData || []);
      setUsers(uData || []);
      const profile = profileData && profileData.user ? profileData.user : profileData;
      setCurrentUser(profile || null);
      setLoadingFlights(false);
    }).catch(err => {
      console.error('Failed to load data', err);
      setLoadingFlights(false);
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    // Require race_id
    if (!raceId) {
      setStatus('Please select a race');
      return;
    }

    const payload = {
      flight_number: flightNumber,
      departure,
      arrival,
      departure_time: departureTime,
      arrival_time: arrivalTime,
      race_id: raceId,
    };

    setStatus('Saving...');
    try {
      const res = await fetch('/api/flights', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setStatus('Flight added');
        setFlightNumber('');
        setDeparture('');
        setArrival('');
        setDepartureTime('');
        setArrivalTime('');
        setRaceId('');
        // refresh flights list
        const newFlights = await fetch('/api/flights', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => []);
        setFlights(newFlights || []);
      } else {
        setStatus(data.msg || 'Failed to add flight');
      }
    } catch (err) {
      console.error(err);
      setStatus('Network error');
    }
  };

  const filteredFlights = flights.filter(f => {
    if (filterRace && String(f.race_id) !== String(filterRace)) return false;
    if (search) {
      const s = search.toLowerCase();
      return (f.flight_number || '').toLowerCase().includes(s) || (f.departure || '').toLowerCase().includes(s) || (f.arrival || '').toLowerCase().includes(s);
    }
    return true;
  });

  const canManageFlight = (f) => currentUser && (currentUser.admin || String(currentUser.id) === String(f.user_id));

  const toIsoLocal = (dt) => {
    if (!dt) return '';
    const d = new Date(dt);
    return d.toISOString().slice(0, 16);
  };

  function startEdit(f) {
    setEditingFlightId(f.id);
    setEditForm({
      flight_number: f.flight_number || '',
      departure: f.departure || '',
      arrival: f.arrival || '',
      departure_time: toIsoLocal(f.departure_time),
      arrival_time: toIsoLocal(f.arrival_time)
    });
  }

  function cancelEdit() {
    setEditingFlightId(null);
    setEditForm({ flight_number: '', departure: '', arrival: '', departure_time: '', arrival_time: '' });
  }

  async function submitEdit(flightId) {
    const token = localStorage.getItem('token');
    if (!token) return;
    const payload = {
      flight_number: editForm.flight_number,
      departure: editForm.departure,
      arrival: editForm.arrival,
      departure_time: editForm.departure_time,
      arrival_time: editForm.arrival_time
    };
    const res = await fetch(`/api/edit_flight/${flightId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload)
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      const newFlights = await fetch('/api/flights', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => []);
      setFlights(newFlights || []);
      cancelEdit();
    } else {
      setStatus(data.msg || 'Edit failed');
    }
  }

  async function deleteFlight(flightId) {
    const token = localStorage.getItem('token');
    if (!token) return;
    const confirmed = window.confirm('Delete this flight?');
    if (!confirmed) return;
    const res = await fetch(`/api/delete_flight/${flightId}`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json().catch(() => ({}));
    if (res.ok) {
      const newFlights = await fetch('/api/flights', { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => []);
      setFlights(newFlights || []);
    } else {
      setStatus(data.msg || 'Delete failed');
    }
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold">Flights</h1>
        <div className="text-sm text-gray-500">Manage flight arrivals and departures</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-3">Add / Assign Flight</h2>
          <form onSubmit={handleSubmit} className="space-y-3">
            <select value={raceId} onChange={e => setRaceId(e.target.value)} className="w-full p-2 border rounded">
              <option value="">— Link to race —</option>
              {races.map(r => <option key={r.id} value={r.id}>{r.name} — {r.location}</option>)}
            </select>

            <input value={flightNumber} onChange={e => setFlightNumber(e.target.value)} placeholder="Flight number" className="w-full p-2 border rounded" />
            <div className="grid grid-cols-2 gap-2">
              <AirportAutocomplete value={departure} onChange={e => setDeparture(e.target.value)} placeholder="Departure (airport)" name="departure" />
              <AirportAutocomplete value={arrival} onChange={e => setArrival(e.target.value)} placeholder="Arrival (airport)" name="arrival" />
            </div>

            <label className="block text-sm text-gray-600">Departure time</label>
            <input value={departureTime} onChange={e => setDepartureTime(e.target.value)} type="datetime-local" className="w-full p-2 border rounded" />
            <label className="block text-sm text-gray-600">Arrival time</label>
            <input value={arrivalTime} onChange={e => setArrivalTime(e.target.value)} type="datetime-local" className="w-full p-2 border rounded" />

            <div className="flex items-center justify-between mt-2">
              <div className="text-sm text-gray-500">{status}</div>
              <button type="submit" className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700">Add flight</button>
            </div>
          </form>
        </div>

        <div className="md:col-span-2">
          <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div className="flex items-center gap-2">
              <select value={filterRace} onChange={e => setFilterRace(e.target.value)} className="p-2 border rounded">
                <option value="">All races</option>
                {races.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
              </select>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search flights, airports..." className="p-2 border rounded w-64" />
            </div>

          </div>

          <div className="space-y-3">
            {filteredFlights.map(f => (
              <div key={f.id} className="bg-white rounded-lg shadow p-4 flex items-center justify-between">
                <div className="flex-1">
                  <div className="text-lg font-medium">{f.flight_number || '—'}</div>
                  <div className="text-sm text-gray-600">{f.departure || '—'} → {f.arrival || '—'}</div>
                  <div className="text-xs text-gray-500 mt-1">{f.departure_time ? new Date(f.departure_time).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'} — {f.arrival_time ? new Date(f.arrival_time).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-'}</div>
                  {f.race_id && <div className="text-xs text-indigo-600 mt-1">Linked to: {races.find(r=>String(r.id)===String(f.race_id))?.name || `Race ${f.race_id}`}</div>}
                  <div className="text-sm text-gray-700 mt-2">Booked by: <span className="font-medium">{(users.find(u=>String(u.id)===String(f.user_id))||{}).name || `User ${f.user_id}`}</span></div>

                  {/* Edit form (inline) */}
                  {canManageFlight(f) && editingFlightId === f.id && (
                    <div className="mt-3 p-3 border rounded bg-gray-50 space-y-2">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <input value={editForm.flight_number} onChange={e => setEditForm(f => ({ ...f, flight_number: e.target.value }))} className="p-2 border rounded" placeholder="Flight number" />
                        <input value={editForm.departure} onChange={e => setEditForm(f => ({ ...f, departure: e.target.value }))} className="p-2 border rounded" placeholder="Departure airport" />
                        <input value={editForm.arrival} onChange={e => setEditForm(f => ({ ...f, arrival: e.target.value }))} className="p-2 border rounded" placeholder="Arrival airport" />
                        <input type="datetime-local" value={editForm.departure_time} onChange={e => setEditForm(f => ({ ...f, departure_time: e.target.value }))} className="p-2 border rounded" />
                        <input type="datetime-local" value={editForm.arrival_time} onChange={e => setEditForm(f => ({ ...f, arrival_time: e.target.value }))} className="p-2 border rounded" />
                      </div>
                      <div className="flex gap-2 justify-end text-sm">
                        <button onClick={cancelEdit} className="px-3 py-1 rounded border text-gray-700">Cancel</button>
                        <button onClick={() => submitEdit(f.id)} className="px-3 py-1 rounded bg-indigo-600 text-white">Save</button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2 ml-4">
                  
                  {canManageFlight(f) && (
                    <div className="flex gap-2">
                      <button onClick={() => startEdit(f)} className="px-3 py-1 bg-yellow-500 text-white rounded text-sm">Edit</button>
                      <button onClick={() => deleteFlight(f.id)} className="px-3 py-1 bg-red-700 text-white rounded text-sm">Delete</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {!loadingFlights && filteredFlights.length === 0 && (
              <div className="bg-white rounded-lg shadow p-4 text-center text-gray-500">No flights found</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Flights;